// Auth de la plataforma, no de las aplicaciones de terceros

import { supabase } from './supabaseClient';

class AuthService {
  async login(email, password) {
    // Normalizar email a minúsculas para login case-insensitive
    const normalizedEmail = email.trim().toLowerCase();

    const { data, error } = await supabase.auth.signInWithPassword({
      email: normalizedEmail,
      password
    });

    if (error) {
      console.error('Error de login:', error.message);
      throw error;
    }

    // El token se sincroniza automáticamente en AuthContext
    // No necesitamos manejarlo manualmente aquí

    return data;
  }

  async register(username, email, password, captchaToken = null, registrationCode = null) {
    console.log('Intento de registro iniciado');

    const normalizedEmail = email.trim().toLowerCase();
    const normalizedUsername = username.trim().toLowerCase();

    const signUpOptions = { data: { username: normalizedUsername } };
    if (captchaToken) signUpOptions.captchaToken = captchaToken;
    // PRP invite-model-3-paths-canonical Bug A (incognito cross-browser):
    // Pass the registration code via emailRedirectTo so it survives the email
    // confirmation flow even when the user opens the email link in a different
    // browser/window than where they originally registered (lost localStorage).
    // AuthContext + TermsConsentPage will check URLSearchParams.flock_invite as
    // a fallback when localStorage is empty.
    if (registrationCode) {
      signUpOptions.emailRedirectTo =
        `${window.location.origin}/?flock_invite=${encodeURIComponent(registrationCode)}`;
    }

    const { data, error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: signUpOptions
    });

    if (error) {
      console.error('Error detallado de registro:', {
        mensaje: error.message,
        código: error.status || error.code,
        usuario: normalizedUsername,
        email: normalizedEmail,
        detalles: error.details || 'No hay detalles adicionales'
      });
      throw error;
    }

    // Supabase no lanza error cuando el email ya existe — responde con éxito silencioso:
    // - Email confirmado → data.user === null, data.session === null
    // - Email sin confirmar (cuenta fantasma) → data.user.identities === []
    // Sin este guard, el frontend muestra la pantalla "revisa tu email" indefinidamente,
    // y en el caso fantasma el código de registro puede consumirse en la cuenta vieja.
    if (!data.user || data.user.identities?.length === 0) {
      const err = new Error('User already registered');
      err.code = 'email_exists';
      throw err;
    }

    console.log(`Registro exitoso - Usuario: ${normalizedUsername}, Email: ${normalizedEmail}`);
    return { ...data, needsConfirmation: !data.session };
  }

  async logout() {
    // 🔐 SECURITY: Clear app-specific sessionStorage BEFORE signOut
    // This ensures no user data persists even if signOut fails
    console.log('[AuthService] 🔐 Clearing sessionStorage on logout');
    sessionStorage.removeItem('oauth_auth_token');
    sessionStorage.removeItem('chatProcessingStatus');

    const { error } = await supabase.auth.signOut();
    // El token se limpia automáticamente en AuthContext
    // No necesitamos manejarlo manualmente aquí
    if (error) {
      console.error('Error al cerrar sesión:', error.message);
      throw error;
    }
  }

  getCurrentUser() {
    return supabase.auth.getUser();
  }

  getToken() {
    return supabase.auth.getSession().then(({ data: { session } }) => {
      return session?.access_token;
    });
  }

  isAuthenticated() {
    return this.getToken().then(token => !!token);
  }

  setAuthHeader() {
    // No necesario con Supabase, ya maneja los headers automáticamente
  }

  /**
   * Sends a password reset email to the user
   * @param {string} email - User's email address
   * @throws {Error} If email sending fails
   */
  async sendPasswordResetEmail(email) {
    const normalizedEmail = email.trim().toLowerCase();
    const redirectTo = `${window.location.origin}/login?mode=reset`;

    const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
      redirectTo
    });

    if (error) {
      console.error('Error enviando email de recuperación:', error.message);
      throw error;
    }
  }

  /**
   * Updates the user's password (requires valid session from reset link)
   * @param {string} newPassword - The new password
   * @throws {Error} If password update fails
   */
  async updatePassword(newPassword) {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      console.error('Error actualizando contraseña:', error);

      // Supabase returns 422 (Unprocessable Entity) when password is the same
      // Error message: "New password should be different from the old password."
      if (error.status === 422 || error.message?.toLowerCase().includes('same') || error.message?.toLowerCase().includes('different')) {
        const enhancedError = new Error('SAME_PASSWORD');
        enhancedError.originalError = error;
        throw enhancedError;
      }

      // Weak password errors
      if (error.message?.toLowerCase().includes('weak') || error.message?.toLowerCase().includes('password')) {
        const enhancedError = new Error('WEAK_PASSWORD');
        enhancedError.originalError = error;
        throw enhancedError;
      }

      throw error;
    }
  }

  /**
   * Updates the user's password after verifying the current one.
   * Re-authenticates via signInWithPassword before calling updateUser.
   * @param {string} currentPassword - The user's current password
   * @param {string} newPassword - The desired new password
   * @throws {Error} WRONG_CURRENT_PASSWORD | SAME_PASSWORD | WEAK_PASSWORD
   */
  async updatePasswordWithVerification(currentPassword, newPassword) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.email) throw new Error('NOT_AUTHENTICATED');

    const { error: authError } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: currentPassword,
    });

    if (authError) {
      const err = new Error('WRONG_CURRENT_PASSWORD');
      err.originalError = authError;
      throw err;
    }

    const { error } = await supabase.auth.updateUser({ password: newPassword });

    if (error) {
      console.error('Error actualizando contraseña:', error);

      if (error.status === 422 || error.message?.toLowerCase().includes('same') || error.message?.toLowerCase().includes('different')) {
        const enhancedError = new Error('SAME_PASSWORD');
        enhancedError.originalError = error;
        throw enhancedError;
      }

      if (error.message?.toLowerCase().includes('weak') || error.message?.toLowerCase().includes('password')) {
        const enhancedError = new Error('WEAK_PASSWORD');
        enhancedError.originalError = error;
        throw enhancedError;
      }

      throw error;
    }
  }
}

const authService = new AuthService();
export default authService; 